import React from 'react';
import { Box, LinearProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@ui/Typography';
import TypographyLink from '@ui/TypographyLink';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import type { AvailableIconTypes } from '@ui/Icon/types';
import ModalV2 from '../../../../Modal/ModalV2';

type MigrationModalListItem = {
  text: string;
  icon: AvailableIconTypes;
};

const SECTIONS: { title: string; items: MigrationModalListItem[] }[] = [
  {
    title: 'Before you convert:',
    items: [
      {
        text: "This cannot be undone — you won't be able to return to the old version of this project.",
        icon: 'fileProject',
      },
      {
        text: 'Your audio clips will be preserved as Takes inside editable sections.',
        icon: 'recording02',
      },
      {
        text: 'Your replacements will be carried over (formatting may vary).',
        icon: 'type',
      },
    ],
  },
  {
    title: "What you'll gain:",
    items: [
      {
        text: 'More expressive voices with controls for tone, emotion, and delivery.',
        icon: 'sliders04',
      },
      {
        text: 'Global languages and translation capability.',
        icon: 'globe02',
      },
    ],
  },
];

interface LegacyProjectMigrationModalProps {
  open: boolean;
  projectName: string;
  helpArticleHref: string;
  onClose: () => void;
  onConvertNow: () => void;
  isConverting?: boolean;
}

const useStyles = makeStyles({ name: 'LegacyProjectMigrationModal' })(
  theme => ({
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
      padding: theme.spacing(5),
      width: 628,
      minHeight: 300,
      maxWidth: '100%',
      backgroundColor: palette.gray_900,
    },
    list: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: theme.spacing(3),
      gap: theme.spacing(1.25),
    },
    listItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.25),
    },
    icon: {
      color: palette.gray_0,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
    sectionTitle: {
      marginBottom: theme.spacing(0.5),
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      gap: theme.spacing(2),
      padding: theme.spacing(3, 4),
      borderTop: `1px solid ${palette.gray_700}`,
      backgroundColor: palette.gray_900,
    },
    footerButton: {
      minWidth: 180,
      padding: theme.spacing(1.25, 2),
    },
    convertingHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(2),
      backgroundColor: palette.gray_800,
      borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    },
    convertingContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      width: 628,
      minHeight: 415,
      maxWidth: '100%',
      padding: theme.spacing(6, 5),
      gap: theme.spacing(1),
      backgroundColor: palette.gray_900,
    },
    progress: {
      width: '100%',
      marginTop: theme.spacing(5),
      borderRadius: theme.spacing(1),
      backgroundColor: palette.mint_300,
      '& .MuiLinearProgress-bar': {
        backgroundColor: palette.mint_600,
      },
    },
  })
);

export default function LegacyProjectMigrationModal({
  open,
  projectName,
  helpArticleHref,
  onClose,
  onConvertNow,
  isConverting,
}: LegacyProjectMigrationModalProps) {
  const { classes } = useStyles();
  const title = `Convert "${projectName}"`;

  return (
    <ModalV2.Modal
      open={open}
      onClose={isConverting ? () => {} : onClose}
      title={title}
      header={
        isConverting ? (
          <Box className={classes.convertingHeader}>
            <Typography component="h1" variant="h5" bold color="tertiary">
              {title}
            </Typography>
          </Box>
        ) : undefined
      }
      withFooterContainer={false}
      footer={
        isConverting ? (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <></>
        ) : (
          <Box className={classes.footer}>
            <Button
              variant="secondary"
              className={classes.footerButton}
              onClick={onClose}
              data-e2e="legacy-project-conversion-modal-cancel-action"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className={classes.footerButton}
              onClick={onConvertNow}
              data-e2e="legacy-project-conversion-modal-convert-action"
            >
              Convert now
            </Button>
          </Box>
        )
      }
    >
      {isConverting ? (
        <Box className={classes.convertingContent}>
          <Box display="flex" flexDirection="column" paddingTop={4} gap={2}>
            <Typography variant="h3" color="gray_0" bold>
              Converting!
            </Typography>
            <Typography variant="pMd" color="gray_100">
              Page will reload. This may take a few minutes.
            </Typography>
          </Box>
          <LinearProgress className={classes.progress} />
        </Box>
      ) : (
        <Box className={classes.content}>
          {SECTIONS.map(({ title: sectionTitle, items }) => (
            <Box className={classes.section} key={sectionTitle}>
              <Typography
                variant="h3"
                color="primary"
                bold
                className={classes.sectionTitle}
              >
                {sectionTitle}
              </Typography>
              <Box className={classes.list}>
                {items.map(item => (
                  <Box className={classes.listItem} key={item.text}>
                    <Icon type={item.icon} className={classes.icon} />
                    <Typography variant="pMd" color="gray_0">
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          <Typography variant="pSm" color="gray_0">
            <TypographyLink
              href={helpArticleHref}
              target="_blank"
              rel="noopener noreferrer"
              underlined
              variant="pSm"
              color="gray_0"
            >
              Learn more
            </TypographyLink>{' '}
            about what happens when you convert.
          </Typography>
        </Box>
      )}
    </ModalV2.Modal>
  );
}
